import { reactive } from 'vue'
import moment from '@/util/MPMoment'

/**
 * 예약 통계 모니터 팝업의 공유 상태.
 *
 * 기간 선택, 필터 4종(그룹·열·프로그램·상태)의 로컬 선택 상태, 조회 결과 캐시를
 * 팝업 내부 컴포넌트 10곳이 공유한다. API 호출은 하지 않는다 (조회는 팝업 루트가 담당).
 * 구 ReservationMonitorPopupDataController 를 1:1 대체한다.
 */

export type MonitorPeriodType = 'day' | 'week' | 'month'

/** 로드된 도메인 항목에 isSelected 플래그를 얹어 로컬 선택 상태로 관리한다 */
export interface SelectableItem {
    _id: string
    isSelected?: boolean
    [key: string]: unknown
}

interface SelectedPeriod {
    periodType: MonitorPeriodType
    startDate: unknown
    endDate: unknown
}

export const monitorState = reactive({
    selectedPeriod: {
        periodType: 'day',
        startDate: moment(new Date()).add(-7, 'days').format('YYYY-MM-DD').toString(),
        endDate: moment(new Date()).format('YYYY-MM-DD').toString()
    } as SelectedPeriod,
    selectedPerType: 'managers', // 담당자: managers, 프로그램별: programs, 상태별: status
    selectedCountType: 'patients', // 환자수: patients, 예약수: reservations

    targetDateArray: [] as unknown[],

    // from network
    loadedGroups: [] as SelectableItem[],
    loadedColumns: [] as SelectableItem[],
    loadedPrograms: [] as SelectableItem[],
    loadedStatus: [] as SelectableItem[],

    // parsed
    tableColumns: {} as Record<string, unknown>,
    tablePrograms: {} as Record<string, unknown>,
    tableStatus: {} as Record<string, unknown>,

    // local selected store
    localGroups: [] as SelectableItem[],
    localColumns: [] as SelectableItem[],
    localPrograms: [] as SelectableItem[],
    localStatus: [] as SelectableItem[],

    rawData: [] as unknown[],
    totalData: [] as unknown[],

    reservationData: [] as unknown[],

    colorArray: [
        '#FF5C71',
        '#FF8A00',
        '#FEC860',
        '#58D87B',
        '#5BD9E8',
        '#329EF1',
        '#977AF4',
        '#F178DE',
        '#4259AA',
        '#6B5353'
    ]
})

const addKey: Record<MonitorPeriodType, { add: number; period: MonitorPeriodType }> = {
    day: { add: -6, period: 'day' },
    week: { add: -5, period: 'week' },
    month: { add: -5, period: 'month' }
}

export function updateSelectedPeriod(periodType: MonitorPeriodType, endDate: unknown): void {
    const { add, period } = addKey[periodType]
    const end = moment(endDate).endOf(period)
    const start = moment(end).startOf(period).add(add, period)
    monitorState.selectedPeriod = {
        periodType,
        startDate: start,
        endDate: end
    }

    monitorState.targetDateArray = []
    let currentDate = moment(start).startOf(period)
    while (currentDate.isSameOrBefore(end)) {
        monitorState.targetDateArray.push(currentDate)
        currentDate = moment(currentDate).add(1, period)
    }
}

/** 조회 필터 — 기간이 준비되지 않았으면 undefined */
export function monitorFilter(): { startDate: string; endDate: string } | undefined {
    const { startDate, endDate } = monitorState.selectedPeriod
    if (startDate == undefined || endDate == undefined) {
        return undefined
    }
    return {
        startDate: moment(startDate).format(),
        endDate: moment(endDate).format()
    }
}

// MARK: - 로컬 선택 상태 (도메인 4종 공통 로직)

/** 새로 로드된 항목만 isSelected=true 로 추가한다 — 기존 항목의 선택 상태는 유지 */
function mergeInto(target: SelectableItem[], items: SelectableItem[]): void {
    for (const item of items) {
        const original = target.find(t => t._id == item._id)
        if (original == undefined) {
            item.isSelected = true
            target.push(item)
        }
    }
}

function setSelected(target: SelectableItem[], item: SelectableItem, isSelected: boolean): void {
    const original = target.find(t => t._id == item._id)
    if (original == undefined) {
        return
    }
    original.isSelected = isSelected
    const index = target.indexOf(original)
    target.splice(index, 1, original)
}

export function mergeLocalGroups(groups: SelectableItem[]): void {
    mergeInto(monitorState.localGroups, groups)
}
export function selectedGroups(): SelectableItem[] {
    return monitorState.localGroups.filter(g => g.isSelected == true)
}
export function setSelectedGroup(group: SelectableItem, isSelected: boolean): void {
    setSelected(monitorState.localGroups, group, isSelected)
}

export function mergeLocalColumns(columns: SelectableItem[]): void {
    mergeInto(monitorState.localColumns, columns)
}
export function selectedColumns(): SelectableItem[] {
    return monitorState.localColumns.filter(c => c.isSelected == true)
}
export function setSelectedColumn(column: SelectableItem, isSelected: boolean): void {
    setSelected(monitorState.localColumns, column, isSelected)
}

export function mergeLocalPrograms(programs: SelectableItem[]): void {
    mergeInto(monitorState.localPrograms, programs)
}
export function selectedPrograms(): SelectableItem[] {
    return monitorState.localPrograms.filter(p => p.isSelected == true)
}
export function setSelectedProgram(program: SelectableItem, isSelected: boolean): void {
    setSelected(monitorState.localPrograms, program, isSelected)
}

export function mergeLocalStatus(status: SelectableItem[]): void {
    mergeInto(monitorState.localStatus, status)
}
export function selectedStatus(): SelectableItem[] {
    return monitorState.localStatus.filter(s => s.isSelected == true)
}
export function setSelectedStatus(status: SelectableItem, isSelected: boolean): void {
    setSelected(monitorState.localStatus, status, isSelected)
}

// 구 컨트롤러 생성자와 동일하게 초기 기간을 계산해 둔다
updateSelectedPeriod(monitorState.selectedPeriod.periodType, monitorState.selectedPeriod.endDate)
